import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './HomeComercial.css';
import NumeroSuerte from './NumeroSuerte';
import { useExtractos, tarjetasResultados, ticker as tickerDeExtractos } from './useExtractos';

// Home comercial "INPROJUY Juega" — port React del mockup dirigido por Fable.
// Animación "cinematográfica pero rápida": entrada del hero, count-up del pozo,
// bolillas en cascada, reveals al scroll. Sin librerías (CSS + IntersectionObserver).
const PUB = process.env.PUBLIC_URL;
const LOGO_BLANCO = `${PUB}/img/logo_inpro_white.png`;

const POZO_OBJETIVO = 17263000000;

const NAV_LINKS = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Juegos', href: '#juegos' },
  { label: 'Cómo se juega', href: '#como-se-juega' },
  { label: 'Resultados', href: '#resultados' },
  { label: 'Dónde jugar', href: '#donde-jugar' },
];

// Logos oficiales scrapeados de loteríalajujeña.com.ar → public/img/juegos/
const JUEGOS = [
  {
    clase: 'hc-g-tombola', freq: 'Todos los días', nombre: 'Tómbola Jujeña',
    logo: `${PUB}/img/juegos/tombola.png`,
    sub: 'La quiniela de Jujuy, cuatro sorteos diarios',
    chips: ['La Previa', 'Matutino', 'La Primera', 'Nocturno'],
  },
  {
    clase: 'hc-g-loto', freq: 'Miércoles y sábados', nombre: 'Loto Plus',
    logo: `${PUB}/img/juegos/lotoplus.png`,
    sub: 'El pozo más grande del país',
    chips: ['Tradicional', 'Match', 'Desquite', 'Sale o Sale'], delay: 'dly-1',
  },
  {
    clase: 'hc-g-quini', freq: 'Miércoles y domingos', nombre: 'Quini 6',
    logo: `${PUB}/img/juegos/quini6.png`,
    sub: 'Seis números para cambiar tu vida',
    chips: ['Tradicional', 'Segunda', 'Revancha', 'Siempre Sale'], delay: 'dly-2',
  },
  {
    clase: 'hc-g-brinco', freq: 'Domingos', nombre: 'Brinco',
    logo: `${PUB}/img/juegos/brinco.png`,
    sub: 'Millones por 40 pesitos',
    chips: ['5 números', 'Sorteo semanal'], delay: 'dly-3',
  },
  {
    clase: 'hc-g-loto5', freq: 'Viernes', nombre: 'Loto 5 Plus',
    logo: `${PUB}/img/juegos/loto5plus.png`,
    sub: 'Cinco números, todos los viernes',
    chips: ['5 aciertos'],
  },
  {
    clase: 'hc-g-pozo', freq: 'Todas las noches', nombre: 'Pozo de la Quiniela',
    logo: `${PUB}/img/juegos/pozo.png`,
    sub: 'El clásico de la noche jujeña, con Super Bola',
    chips: ['Super Bola'], delay: 'dly-1',
  },
  {
    clase: 'hc-g-telekino', freq: 'Domingos', nombre: 'Telekino',
    logo: `${PUB}/img/juegos/telekino.png`,
    sub: 'El cartón que sortea todos los domingos',
    chips: ['Cartón', 'Sorteo TV'], delay: 'dly-2',
  },
  {
    clase: 'hc-g-loteria', freq: 'Sorteo semanal', nombre: 'Lotería',
    logo: `${PUB}/img/juegos/loteria.png`,
    sub: 'La lotería tradicional unificada',
    chips: ['Billete', 'Premio mayor'], delay: 'dly-3',
  },
];

const RESULTADOS = [
  {
    nombre: 'La Primera', detalle: 'Nº 5095 · 12:00',
    numeros: ['4133', '1214', '5433', '3112', '6115', '8447'],
  },
  {
    nombre: 'Pozo Nocturno', detalle: 'Super Bola',
    numeros: ['377', '906', '851', '928', '874'], oro: '0',
  },
  {
    nombre: 'Loto 6', detalle: 'Nº 5095 · Tradicional',
    numeros: ['09', '12', '27', '32', '37'], oro: '44',
  },
];

const BOLILLAS = ['09', '12', '27', '32', '37'];

// Ticker de resultados (marquesina continua)
const TICKER = [
  'La Previa 8829', 'Matutino 9505', 'La Primera 5195', 'Nocturno 0212',
  'Pozo Nocturno 377·906·851·928·874', 'Loto Plus $17.263M', 'Brinco $280M', 'Loto 5 $47M',
];

const BOLAS_FLOTANTES = [
  { n: '07', top: '12%', left: '4%', dur: '11s', delay: '0s', size: 54 },
  { n: '23', top: '68%', left: '9%', dur: '14s', delay: '-4s', size: 40 },
  { n: '48', top: '26%', left: '46%', dur: '12s', delay: '-7s', size: 34 },
  { n: '15', top: '80%', left: '52%', dur: '16s', delay: '-2s', size: 46 },
  { n: '90', top: '8%', left: '88%', dur: '13s', delay: '-9s', size: 38 },
  { n: '33', top: '58%', left: '93%', dur: '15s', delay: '-5s', size: 50 },
];

const formatoARS = new Intl.NumberFormat('es-AR');

const HomeComercial = () => {
  const [navSolida, setNavSolida] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [videoOk, setVideoOk] = useState(true);

  // Extractos reales desde Firestore. Mientras no lleguen —o si algo falla—
  // se muestran los de ejemplo, así la página nunca aparece vacía.
  const extractos = useExtractos();
  const resultados = tarjetasResultados(extractos) || RESULTADOS;
  const tickerItems = tickerDeExtractos(extractos) || TICKER;
  const fechaExtracto = extractos?.fecha || null;
  const pageRef = useRef(null);
  // El monto del pozo se escribe directo al DOM (sin re-render por frame):
  // con setState por frame el video de fondo se trababa.
  const pozoRef = useRef(null);
  const videoRef = useRef(null);

  // Video de fondo indetenible:
  // 1) El tamaño se congela en píxeles al montar (ningún reflow del hero
  //    puede redimensionarlo y disparar la pausa del navegador).
  // 2) Si igual se pausa, el evento `pause` lo reanuda al instante.
  // 3) Watchdog cada 700ms como última red (pausas y congelamientos).
  useEffect(() => {
    if (!videoOk) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;

    const congelarTamano = () => {
      const hero = v.closest('.hc-hero');
      if (hero) {
        v.style.width = `${hero.offsetWidth}px`;
        v.style.height = `${hero.offsetHeight}px`;
      }
    };
    congelarTamano();
    window.addEventListener('resize', congelarTamano);

    const reanudar = () => {
      if (document.visibilityState !== 'hidden') v.play().catch(() => {});
    };
    v.addEventListener('pause', reanudar);
    v.addEventListener('stalled', reanudar);
    v.addEventListener('suspend', reanudar);

    let ultimoTiempo = -1;
    const id = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (v.paused || v.ended) {
        v.play().catch(() => {});
      } else if (v.currentTime === ultimoTiempo && !v.seeking && v.readyState >= 2) {
        v.currentTime = 0;
        v.play().catch(() => {});
      }
      ultimoTiempo = v.currentTime;
    }, 700);

    return () => {
      window.removeEventListener('resize', congelarTamano);
      v.removeEventListener('pause', reanudar);
      v.removeEventListener('stalled', reanudar);
      v.removeEventListener('suspend', reanudar);
      clearInterval(id);
    };
  }, [videoOk]);

  // Nav que se condensa al scrollear
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setNavSolida(window.scrollY > 24);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Count-up del pozo: aterriza exacto en POZO_OBJETIVO.
  // Escribe textContent directo (sin setState) para no trabar el video de fondo.
  useEffect(() => {
    const nodo = pozoRef.current;
    if (!nodo) return undefined;
    const escribir = (n) => { nodo.textContent = `$${formatoARS.format(Math.round(n))}`; };
    const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducido) {
      escribir(POZO_OBJETIVO);
      return undefined;
    }
    // ~11 actualizaciones por segundo (no 60): el texto cambiando por frame
    // generaba reflows que trababan el video de fondo.
    const DURACION = 1600;
    const PASO_MS = 90;
    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    let intervalo;
    const inicio = { t: null };
    const timer = setTimeout(() => {
      inicio.t = performance.now();
      intervalo = setInterval(() => {
        const p = Math.min((performance.now() - inicio.t) / DURACION, 1);
        escribir(POZO_OBJETIVO * easeOutExpo(p));
        if (p >= 1) clearInterval(intervalo);
      }, PASO_MS);
    }, 420);
    // Red de seguridad: el monto final queda garantizado igual.
    const garantia = setTimeout(() => escribir(POZO_OBJETIVO), 2600);
    return () => {
      clearTimeout(timer);
      clearTimeout(garantia);
      if (intervalo) clearInterval(intervalo);
    };
  }, []);

  // Reveals al scroll
  useEffect(() => {
    const nodos = pageRef.current?.querySelectorAll('.hc-reveal') ?? [];
    const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducido || !('IntersectionObserver' in window)) {
      nodos.forEach((n) => n.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    nodos.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  // Bloquear scroll del body con el menú móvil abierto
  useEffect(() => {
    document.body.style.overflow = menuAbierto ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuAbierto]);

  const cerrarMenu = () => setMenuAbierto(false);

  return (
    <div className="hc-page" ref={pageRef} id="inicio">
      <div className="hc-serrania" aria-hidden="true"></div>

      <header className={`hc-nav${navSolida || menuAbierto ? ' is-solid' : ''}${menuAbierto ? ' menu-open' : ''}`}>
        <div className="hc-nav-inner">
          <a href="#inicio" className="hc-logo" aria-label="INPROJUY Juega — inicio">
            <img src={LOGO_BLANCO} alt="INPROJUY" />
            <span className="hc-tag">Juega</span>
          </a>
          <nav className="hc-nav-links" aria-label="Navegación principal">
            {NAV_LINKS.map((l) => <a key={l.label} href={l.href}>{l.label}</a>)}
          </nav>
          <div className="hc-nav-cta">
            <Link to="/login" className="hc-btn hc-btn-ghost">Ingresar</Link>
            <a href="#donde-jugar" className="hc-btn hc-btn-coral">Jugar ahora</a>
          </div>
          <button
            className="hc-burger"
            aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuAbierto}
            onClick={() => setMenuAbierto((v) => !v)}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      <div className={`hc-mobile-menu${menuAbierto ? ' open' : ''}`}>
        {NAV_LINKS.map((l) => <a key={l.label} href={l.href} onClick={cerrarMenu}>{l.label}</a>)}
        <div className="hc-mm-cta">
          <Link to="/login" className="hc-btn hc-btn-ghost" onClick={cerrarMenu}>Ingresar</Link>
          <a href="#donde-jugar" className="hc-btn hc-btn-coral" onClick={cerrarMenu}>Jugar ahora</a>
        </div>
      </div>

      <section className="hc-hero">
        {/* Video de fondo opcional: poné el mp4 en public/video/hero-bg.mp4 y aparece solo */}
        {videoOk && (
          <>
            <video
              ref={videoRef}
              className="hc-hero-video"
              autoPlay muted loop playsInline
              preload="auto"
              onError={() => setVideoOk(false)}
              src={`${PUB}/video/hero-bg.mp4`}
            />
            <div className="hc-hero-velo" aria-hidden="true"></div>
          </>
        )}
        <div className="hc-bolas-flotantes" aria-hidden="true">
          {BOLAS_FLOTANTES.map((b) => (
            <span
              key={b.n}
              className="hc-bola-flotante"
              style={{
                top: b.top, left: b.left,
                width: b.size, height: b.size, fontSize: b.size * 0.38,
                animationDuration: b.dur, animationDelay: b.delay,
              }}
            >
              {b.n}
            </span>
          ))}
        </div>
        <div className="hc-wrap hc-hero-grid">
          <div>
            <span className="hc-eyebrow hc-enter">Juegos oficiales de la Provincia de Jujuy</span>
            <h1 className="hc-enter d1">Jugá, mirá los extractos y hacete <span className="hc-gold">millonario</span></h1>
            <p className="hc-hero-sub hc-enter d2">
              Resultados al instante, juegos regulados por INPROJUY y agencias oficiales
              en toda la provincia. Todo lo que necesitás, en un solo lugar.
            </p>
            <div className="hc-hero-ctas hc-enter d3">
              <a href="#resultados" className="hc-btn hc-btn-gold">Ver resultados de hoy</a>
              <a href="#como-se-juega" className="hc-btn hc-btn-ghost">Cómo se juega</a>
            </div>
            <div className="hc-trust-row hc-enter d4">
              <div className="hc-trust-item"><strong>+120</strong><span>agencias oficiales</span></div>
              <div className="hc-trust-item"><strong>7</strong><span>juegos oficiales</span></div>
              <div className="hc-trust-item"><strong>Todos los días</strong><span>resultados publicados</span></div>
            </div>
          </div>

          <aside className="hc-jackpot hc-enter d5" aria-label="Pozo destacado: Loto Plus">
            <div className="hc-jackpot-inner">
              <div className="hc-jp-head">
                <span className="hc-jp-game"><em>Loto</em> Plus</span>
                <span className="hc-jp-mod">Tradicional + Plus</span>
              </div>
              <p className="hc-jp-label">Pozo estimado</p>
              <p className="hc-jp-amount" ref={pozoRef} aria-label="17.263 millones de pesos">
                $0
              </p>
              <p className="hc-jp-meta">
                <span>Sorteo <b>Nº 5095</b></span>
                <span>Próximo: <b>domingo 05/08</b></span>
              </p>
              <div className="hc-jp-balls" aria-label="Últimos números sorteados">
                {BOLILLAS.map((n) => <span key={n} className="hc-ball">{n}</span>)}
                <span className="hc-ball hot">44</span>
              </div>
              <div className="hc-jp-foot">
                <a href="#resultados">Ver último extracto <span aria-hidden="true">→</span></a>
                <span className="hc-draw-note">Sorteos en vivo por Lotería Jujeña</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <div className="hc-serrania" aria-hidden="true"></div>

      <div className="hc-ticker" aria-hidden="true">
        <div className="hc-ticker-track">
          {[...tickerItems, ...tickerItems].map((t, i) => (
            <span className="hc-ticker-item" key={i}>{t}<em>◆</em></span>
          ))}
        </div>
      </div>

      <section className="hc-section" id="juegos">
        <div className="hc-wrap">
          <div className="hc-sec-head hc-reveal">
            <div>
              <p className="hc-sec-kicker">Juegos oficiales</p>
              <h2>Elegí tu juego</h2>
            </div>
            <a href="#juegos" className="hc-sec-link" id="como-se-juega">
              Ver todas las modalidades <span aria-hidden="true">→</span>
            </a>
          </div>
          <div className="hc-games-grid">
            {JUEGOS.map((j) => (
              <article key={j.nombre} className={`hc-game-card ${j.clase} hc-reveal${j.delay ? ` ${j.delay}` : ''}`}>
                <div className="hc-gc-top">
                  <span className="hc-gc-freq">{j.freq}</span>
                  <img className="hc-gc-logo" src={j.logo} alt={j.nombre} loading="lazy" />
                </div>
                <div className="hc-gc-body">
                  <h3>{j.nombre}</h3>
                  <span className="hc-gc-sub">{j.sub}</span>
                  <ul className="hc-gc-chips">
                    {j.chips.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                  <a href="#como-se-juega" className="hc-gc-link">Cómo se juega <span aria-hidden="true">→</span></a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="hc-section hc-results" id="resultados">
        <div className="hc-wrap">
          <div className="hc-sec-head hc-reveal">
            <div>
              <p className="hc-sec-kicker">Últimos resultados</p>
              <h2>Extractos del día</h2>
            </div>
            <p>Resultados oficiales publicados por Lotería Jujeña apenas termina cada sorteo.</p>
          </div>

          <div className="hc-board hc-reveal dly-1">
            <div className="hc-board-bar">
              <h3>Extractos del día{fechaExtracto ? <> · <b>Sorteo del {fechaExtracto}</b></> : null}</h3>
              <span className="hc-live-dot">Actualizado</span>
            </div>

            {resultados.map((r) => (
              <div className="hc-board-row" key={r.nombre}>
                <div className="hc-br-name">
                  <strong>{r.nombre}</strong>
                  <span>{r.detalle}</span>
                </div>
                <div className="hc-tiles" aria-label={`Números sorteados de ${r.nombre}`}>
                  {r.numeros.map((n) => <span key={n} className="hc-tile">{n}</span>)}
                  {r.oro !== undefined && <span className="hc-tile gold">{r.oro}</span>}
                </div>
                <a href="#resultados" className="hc-br-link">Ver extracto <span aria-hidden="true">→</span></a>
              </div>
            ))}

            <div className="hc-board-foot">
              <a href="#resultados" id="donde-jugar">Ver todos los extractos <span aria-hidden="true">→</span></a>
            </div>
          </div>
        </div>
      </section>

      <div className="hc-serrania" aria-hidden="true"></div>

      <footer className="hc-footer">
        <div className="hc-wrap">
          <div className="hc-footer-grid">
            <div className="hc-footer-brand">
              <a href="#inicio" className="hc-logo" aria-label="INPROJUY Juega — inicio">
                <img src={LOGO_BLANCO} alt="INPROJUY" />
              </a>
              <p>Instituto Provincial de Juegos de Azar de Jujuy. Juegos regulados, resultados oficiales de Lotería Jujeña.</p>
            </div>
            <div className="hc-footer-links">
              <div>
                <h4>Jugá con confianza</h4>
                <Link to="/juegoResponsable">Juego responsable</Link>
                <Link to="/formulario">Autoexclusión</Link>
              </div>
              <div>
                <h4>Accesos</h4>
                <Link to="/agencias">Agencias</Link>
                <Link to="/login">Ingresar</Link>
              </div>
            </div>
            <div className="hc-pill-18">
              <span className="hc-badge">18+</span>
              Prohibido para menores de 18 años
            </div>
          </div>
          <div className="hc-footer-bottom">
            <small>© 2026 INPROJUY · Provincia de Jujuy, Argentina</small>
            <small>Jugar compulsivamente es perjudicial. Línea de ayuda: 0800-333-0333</small>
          </div>
        </div>
      </footer>

      <NumeroSuerte />
    </div>
  );
};

export default HomeComercial;
